/** @jsxImportSource theme-ui */
import React, { useRef } from 'react';
import { Input } from 'semantic-ui-react';
import { ThemeUIStyleObject } from 'theme-ui';

import { sxtheme } from 'utils/cssHelpers';

import BdIcon, { InsightsIconLiteral } from 'components/_common/BdIcon/BdIcon';
import { Typography } from 'components/_common/Typography';
import { TypographyColor } from 'components/_common/Typography/Typography';

import { IconButton } from '../IconButton';

const inputStyles = (bottomOnly: boolean): ThemeUIStyleObject => {
  function borderStyle(color: TypographyColor) {
    if (bottomOnly) {
      return { border: 'unset', borderBottom: sxtheme(t => `1px solid ${t.colors[color]}`) };
    }

    return { border: sxtheme(t => `1px solid ${t.colors[color]}`) };
  }

  return {
    '&.ui.input > input': {
      ...borderStyle('stone20'),
      height: '48px',
      borderRadius: '2px',
      color: sxtheme(t => t.colors.stone90),
      py: '12px',
      pl: '16px',
      pr: '56px !important', // 32 px for padding between clear icon, 24px for icon width
      ':focus': bottomOnly
        ? { boxShadow: sxtheme(t => `inset 0 0 0 1px ${t.colors.deepSea60}`) }
        : borderStyle('deepSea60'),
      '::placeholder': {
        color: sxtheme(t => `${t.colors.stone50}`),
      },
    },
    '&.ui.input.error > input': borderStyle('starfish100'),
    '&.ui.disabled.input': {
      opacity: 1,
      ' > input': {
        backgroundColor: sxtheme(t => t.colors.stone10),
      },
    },
    '&.ui.icon.input > i.icon': {
      opacity: 1,
    },
  };
};

type BdInputProps = {
  /** The shadow placeholder in the input. This is the fallback for the aria-label if no label text is supplied */
  placeholder: string;
  /** The actual text inside the input */
  value: string;
  /** Emits the updated value of the input */
  onChange: (newVal: string) => void;
  /** Sets the state to loading and a loading indicator will appear */
  isLoading?: boolean;
  disabled?: boolean;
  /** Optional label text. If this is present it will use this as the aria-label */
  labelText?: string;
  /**
   * Determines the colour of the label.
   *
   * Low: stone50, Medium: Stone50, High: Stone90
   */
  labelEmphasis?: 'low' | 'medium' | 'high';
  /** Optional icon to put inside the input */
  iconName?: InsightsIconLiteral;
  /** Any error text associated. If there is an error the input will focus it's error state */
  error?: string;
  /** Ensures the border only appears at the bottom of the input */
  bottomBorderOnly?: boolean;
};

// Figma Defined In: https://www.figma.com/file/qPOzRrY6IHiGu9g3n6lvWq/Insights-Design-System-V2.0-Structure-Exploration?node-id=653%3A1851
const BdInput: React.FC<BdInputProps> = ({
  placeholder,
  value,
  onChange,
  isLoading = false,
  disabled = false,
  labelText = null,
  labelEmphasis = 'high',
  iconName = null,
  error = null,
  bottomBorderOnly = false,
  ...props
}) => {
  const inputRef = useRef(null);

  const labelColor: TypographyColor =
    labelEmphasis === 'high' ? 'stone90' : labelEmphasis === 'medium' ? 'stone70' : 'stone50';

  const clear = () => {
    onChange('');
    inputRef.current.focus();
  };

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <div {...props}>
      {!!labelText?.length && (
        <Typography variant="body2" color={disabled ? 'stone50' : labelColor} marginBottom="8px">
          {labelText}
        </Typography>
      )}
      <div sx={{ position: 'relative' }}>
        <Input
          data-testid="bd-input"
          aria-label={labelText?.length ? labelText : placeholder}
          ref={inputRef}
          icon={
            iconName ? (
              <BdIcon
                data-testid="bd-input-icon"
                name={iconName}
                color={disabled ? 'stone50' : 'sea90'}
                bold
                sx={{ mr: '8px' }}
              />
            ) : null
          }
          iconPosition={iconName || isLoading ? 'left' : null}
          placeholder={placeholder}
          fluid
          value={value}
          onChange={e => !disabled && onChange(e.target.value)}
          loading={isLoading}
          error={!!error?.length}
          disabled={disabled}
          sx={inputStyles(bottomBorderOnly)}
        />
        {!!value.length && !disabled && (
          <IconButton
            ariaLabel="clear text"
            icon="icon-close"
            color="sea90"
            size="medium"
            sx={{
              position: 'absolute',
              top: '12px',
              right: '16px',
            }}
            onClick={clear}
          />
        )}
      </div>
      {!!error?.length && (
        <Typography variant="caption" color="starfish100" sx={{ mt: '8px', mb: 0 }}>
          {error}
        </Typography>
      )}
    </div>
  );
};

export default BdInput;
